/**
 * Error Logging Utility
 * 
 * Centralized error logging that can be configured to send errors to various services.
 * Currently logs to console with structured data. Can be extended to use Sentry, LogRocket, etc.
 * 
 * Usage:
 * 1. Set VITE_SENTRY_DSN in your .env file for Sentry integration
 * 2. Call initErrorLogging() in your app entry point
 * 3. Use logError() throughout your app for consistent error handling
 */

interface ErrorContext {
  componentStack?: string;
  userId?: string;
  route?: string;
  action?: string;
  extra?: Record<string, any>;
}

interface LoggedError {
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  context?: ErrorContext;
}

// Error log storage for debugging (keeps last 50 errors)
const errorLog: LoggedError[] = [];
const MAX_ERROR_LOG_SIZE = 50;

/**
 * Initialize error logging
 * Call this in your app entry point (main.tsx)
 */
export function initErrorLogging(): void {
  // Global error handler for uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    logError(error || new Error(String(message)), {
      extra: { source, lineno, colno }
    });
    return false; // Let the error propagate
  };

  // Global handler for unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    logError(error, { action: 'unhandledRejection' });
  };

  // Check for Sentry DSN
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (sentryDsn) {
    console.info('[ErrorLogging] Sentry DSN detected. To enable Sentry, install @sentry/react and initialize it.');
    // TODO: Initialize Sentry when package is installed
    // import * as Sentry from '@sentry/react';
    // Sentry.init({ dsn: sentryDsn, environment: import.meta.env.MODE });
  }

  console.info('[ErrorLogging] Error logging initialized');
}

/**
 * Log an error with optional context
 */
export function logError(error: Error, context?: ErrorContext): void {
  const loggedError: LoggedError = {
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    context,
  };

  // Store in local error log
  errorLog.push(loggedError);
  if (errorLog.length > MAX_ERROR_LOG_SIZE) {
    errorLog.shift();
  }

  // Console logging with structured data
  console.group(`🚨 Error: ${error.message}`);
  console.error('Error:', error);
  console.info('Timestamp:', loggedError.timestamp);
  console.info('URL:', loggedError.url);
  if (context) {
    console.info('Context:', context);
  }
  console.groupEnd();

  // Send to external service if configured
  sendToExternalService(loggedError);
}

/**
 * Log a warning (non-critical issue)
 */
export function logWarning(message: string, context?: Record<string, any>): void {
  console.warn(`⚠️ Warning: ${message}`, context || '');
}

/**
 * Log an info message for tracking
 */
export function logInfo(message: string, context?: Record<string, any>): void {
  if (import.meta.env.DEV) {
    console.info(`ℹ️ ${message}`, context || '');
  }
}

/**
 * Get recent errors (for debugging)
 */
export function getRecentErrors(): LoggedError[] {
  return [...errorLog];
}

/**
 * Clear error log
 */
export function clearErrorLog(): void {
  errorLog.length = 0;
}

/**
 * Send error to external logging service
 */
async function sendToExternalService(error: LoggedError): Promise<void> {
  // Skip in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_LOG_ERRORS_IN_DEV) {
    return;
  }

  // TODO: Uncomment when Sentry is installed
  // import * as Sentry from '@sentry/react';
  // Sentry.captureException(new Error(error.message), {
  //   extra: error.context,
  // });

  // Alternative: Send to custom endpoint
  const logEndpoint = import.meta.env.VITE_ERROR_LOG_ENDPOINT;
  if (logEndpoint) {
    try {
      await fetch(logEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(error),
      });
    } catch (e) {
      // Silently fail - don't create infinite error loop
      console.warn('Failed to send error to logging service');
    }
  }
}

/**
 * Create an error boundary logger for React components
 * Use with ErrorBoundary's componentDidCatch
 */
export function logComponentError(error: Error, componentStack: string): void {
  logError(error, {
    componentStack,
    action: 'componentError',
  });
}

// Performance tracking helper
export function trackPerformance(name: string, fn: () => void): void {
  const start = performance.now();
  fn();
  const duration = performance.now() - start;
  
  if (duration > 100) { // Log slow operations
    logWarning(`Slow operation: ${name}`, { duration: `${duration.toFixed(2)}ms` });
  }
}
